import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { keyframes } from '@mui/system';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';

// Use the correct asset name/path; keep transparency of the PNG
import SpellingHeroLogo from '../../../assets/img/SpelllingHero.png';

// Define keyframe animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(34, 85, 50, 0.1);
  }
  50% {
    box-shadow: 0 0 40px rgba(34, 85, 50, 0.2);
  }
`;

const AboutUs: React.FC = () => {
  const theme = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>About Spelling Hero — Spelling & Vocabulary Mastery</title>
        <link rel="canonical" href="https://spellinghero.app/about-us" />
        <meta
          name="description"
          content="Spelling Hero's story, approach, and mission—rooted in education, powered by innovation, and committed to global learning success."
        />
      </Helmet>

      <Navbar currentPage="aboutus" />

      {/* Landing-style single section */}
      <Box
        sx={{
          background: `
            radial-gradient(circle at 20% 50%, rgba(141, 223, 150, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(220, 244, 223, 0.4) 0%, transparent 50%),
            linear-gradient(157.79deg, #DCF4DF 0%, #8DDF96 100%)
          `,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          pt: { xs: '80px', md: '90px' },
          pb: { xs: 2, md: 3 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 70% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 30% 40%, rgba(34, 85, 50, 0.05) 0%, transparent 50%)
            `,
            pointerEvents: 'none',
          }
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: { xs: 2, md: 3 },
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Header */}
          <Stack 
            spacing={1} 
            alignItems="center" 
            textAlign="center"
            sx={{
              animation: `${fadeInUp} 0.8s ease-out`,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color: '#1d3928',
                letterSpacing: 0.4,
                fontSize: { xs: '1.7rem', md: '2.3rem' },
                lineHeight: 1.1,
                mb: 1,
                background: 'linear-gradient(135deg, #1d3928 0%, #225532 50%, #2a6b3a 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 10px rgba(29, 57, 40, 0.1)',
              }}
            >
              About Spelling Hero
            </Typography>
          </Stack>

          {/* Content */}
          <Grid container spacing={{ xs: 2, md: 3 }} alignItems="stretch">
            {/* Left: Logo */}
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  height: '100%',
                  borderRadius: 0,
                  background: 'transparent',
                  backdropFilter: 'none',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'visible',
                  animation: `${fadeInLeft} 0.8s ease-out 0.2s both`,
                  transition: 'none',
                  '&::before': {
                    display: 'none',
                  },
                  '&:hover': {
                    transform: 'none',
                    boxShadow: 'none',
                  }
                }}
              >
                <Box
                  component="img"
                  src={SpellingHeroLogo}
                  alt="Spelling Hero"
                  sx={{
                    width: '100%',
                    maxWidth: 450,
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                    borderRadius: 2,
                    backgroundColor: 'transparent',
                    animation: `${float} 3s ease-in-out infinite`,
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    }
                  }}
                />
              </Paper>
            </Grid>

            {/* Right: Text cards */}
            <Grid item xs={12} md={7}>
              <Stack spacing={{ xs: 2, md: 2.5 }} sx={{ height: '100%' }}>
                {[
                  {
                    title: "Rooted in Education",
                    content: "In a world where communication is key, mastering spelling at an early age is crucial. Spelling Hero was born out of the need to provide a fun, engaging, and effective way for learners worldwide to enhance their spelling skills. Our dedicated team leverages their passion for education and technology to make spelling not just a rote learning task, but an enjoyable journey.",
                    delay: "0.4s"
                  },
                  {
                    title: "Innovation at the Forefront", 
                    content: "At the heart of Spelling Hero is its unique approach to teaching spelling through Indian, US, and UK accents, catering to a global audience. This innovative app is not just a learning tool, but a gamified experience that keeps users engaged. It uses AI to provide personalized word recommendations based on each user's previous mistakes, helping them improve over time. It also conducts phonogram analysis with color-coded feedback for practice. Another key feature is its ability to allow users to attempt previous mistakes while not repeating the correctly attempted words, ensuring a dynamic and effective learning process.",
                    delay: "0.6s"
                  },
                  {
                    title: "Expanding Horizons",
                    content: "Spelling Hero is more than an app; it's a global community making learning enjoyable. As we grow, we aim to reach more learners, making Spelling Hero synonymous with fun, effective learning, and academic success.",
                    delay: "0.8s"
                  }
                ].map((card, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      borderRadius: 4,
                      background: `
                        linear-gradient(145deg, 
                          rgba(255,255,255,0.95) 0%, 
                          rgba(255,255,255,0.85) 50%,
                          rgba(255,255,255,0.8) 100%
                        )
                      `,
                      border: '1px solid rgba(255,255,255,0.3)',
                      backdropFilter: 'blur(20px)',
                      position: 'relative',
                      overflow: 'hidden',
                      animation: `${fadeInRight} 0.8s ease-out ${card.delay} both`,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(45deg, transparent 30%, rgba(141, 223, 150, 0.1) 50%, transparent 70%)',
                        transform: 'translateX(-100%)',
                        transition: 'transform 0.8s',
                      },
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `
                          0 15px 35px rgba(34, 85, 50, 0.12),
                          0 8px 15px rgba(34, 85, 50, 0.08)
                        `,
                        '&::before': {
                          transform: 'translateX(100%)',
                        }
                      }
                    }}
                  >
                    <Typography 
                      variant="h5" 
                      fontWeight={800} 
                      gutterBottom
                      sx={{ 
                        mb: 1.5,
                        background: 'linear-gradient(135deg, #1d3928 0%, #225532 70%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: -4,
                          left: 0,
                          width: '40px',
                          height: '3px',
                          background: 'linear-gradient(90deg, #8DDF96, #DCF4DF)',
                          borderRadius: '2px',
                        }
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ 
                        lineHeight: 1.6,
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      {card.content}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Footer />
    </>
  );
};

export default AboutUs;
